import React, { useEffect, useState, useCallback } from 'react'
import { connect } from 'react-redux'
import ClientOnlyPortal from '@chegg-wt/core/components/ClientOnlyPortal'
import { AppState } from '@chegg-wt/core/state/types'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import { UPGRADE_MODAL } from '@chegg-wt/core/state/modals/constants'
import { modalDataSelector } from '@chegg-wt/core/state/modals/selectors'
import { closeModal } from '@chegg-wt/core/state/modals/actions'
import {
  writingFormSubmit,
  formSubmitStarted,
  formSubmitStopped,
} from '@chegg-wt/core/state/form/actions'
import { currentStyleSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { removeUpgradeCitationFromSessionStorage } from '@chegg-wt/core/utils/upgrade'
import { Config } from '@chegg-wt/core/config'
import {
  emitComponentViewEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import {
  ComponentPayload,
  componentOrigins,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import { createCitationSourceTypeSelector } from '@chegg-wt/core/state/createCitation/selectors'
import {
  UserClickPayload,
  origins,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { startAuthFlow } from '@chegg-wt/core/state/user/actions'
import { AuthPayload } from '@chegg-wt/core/types/auth'
import { useRoutesContext } from '../../RoutesContext'
import AppLink from '../../AppLink'
import { AuthModalOrigin } from '../AuthModal/types'
import {
  FOOTER_BANNER_CTA_TEXT,
  FOOTER_BANNER_TEXT,
  UCF_UPGRADE_MODAL_ID,
  FREE_CTA_TEXT,
  UPGRADE_MODAL_TITLE,
  FREE_STYLE_TEXT,
} from './constants'
import { HackyModal, ModalContents, FreeCTA, HackyFooter, KeepCitingCTA } from './styled'
import { SmallNotificationBanner } from './SmallNotificationBanner'
import { FREE_CTA_DATA_TEST } from './testIds'

interface UCFUpgradeModalProps {
  isLoggedIn: boolean
  modalData: {
    withAuthBanner: boolean
    alwaysParticipate?: boolean
  }
  projectStyle: string
  currentSourceType: string
  closeModal: (name: string, origin?: string) => void
  writingFormSubmit: ({ citation, initialCitationData }) => void
  formSubmitStopped: () => void
  formSubmitStarted: () => void
  emitComponentViewEvent: (payload: ComponentPayload) => void
  userClick: (payload: UserClickPayload) => void
  startAuthFlow: (payload: AuthPayload) => void
}

const MODAL_ANIMATION_DURATION_MS = 300

export const UCFUpgradeModal: React.FC<UCFUpgradeModalProps> = ({
  isLoggedIn,
  projectStyle,
  currentSourceType,

  closeModal,
  modalData,
  formSubmitStopped,
  formSubmitStarted,
  emitComponentViewEvent,
  userClick,
  startAuthFlow,
}) => {
  const { currentStyleName, ctfmUpgradeCTAText } = Config
  const { toUpgrade } = useRoutesContext()
  const [isOpen, setIsOpen] = useState(true)
  const [isSAModalOpen, setIsSAModalOpen] = useState(false)

  const onSignInClick = () => {
    formSubmitStarted()

    setIsOpen(false)
    setIsSAModalOpen(true)
  }

  useEffect(() => {
    const isContinuing = isSAModalOpen
    const isBailingOut = !isOpen && !isContinuing
    if (isBailingOut) {
      formSubmitStopped()
    }
  }, [isOpen, isSAModalOpen])

  useEffect(() => {
    if (!isSAModalOpen) {
      return
    }
    const asyncOpenId = setTimeout(() => {
      startAuthFlow({
        defaultFlow: 'signin',
        origin: AuthModalOrigin.UPGRADE_MODAL,
        alwaysParticipate: modalData.alwaysParticipate,
      })
    }, MODAL_ANIMATION_DURATION_MS)

    return () => {
      clearTimeout(asyncOpenId)
    }
  }, [isSAModalOpen])

  useEffect(() => {
    if (isOpen) {
      let metadata
      if (currentSourceType) {
        metadata = { source: currentSourceType, style: projectStyle }
      } else {
        metadata = { style: projectStyle }
      }
      emitComponentViewEvent({
        origin: componentOrigins.ucfUpgradeModalOpen,
        metadata,
      })
      return
    }

    const asyncCloseId = setTimeout(
      () => !isSAModalOpen && closeModal(UPGRADE_MODAL),
      MODAL_ANIMATION_DURATION_MS
    )

    return () => {
      clearTimeout(asyncCloseId)
    }
  }, [isOpen])

  const closeHackyModal = useCallback(() => {
    setIsOpen(false)
    removeUpgradeCitationFromSessionStorage()
  }, [])

  const handleFreeCTAClick = () => {
    userClick({ origin: origins.freeCTAUpgradeClick })
  }

  const ctaText = ctfmUpgradeCTAText ?? FREE_CTA_TEXT

  return (
    <ClientOnlyPortal>
      <HackyModal
        id={UCF_UPGRADE_MODAL_ID}
        isOpen={isOpen}
        onClose={closeHackyModal}
        shouldCloseOnClickOutside
        title={UPGRADE_MODAL_TITLE}
        hideCloseButton
      >
        <>
          <ModalContents>
            <AppLink route={toUpgrade()}>
              <FreeCTA
                href="I'm needed because TS complains otherwise, AppLink replaces me when rendering"
                data-test={FREE_CTA_DATA_TEST}
                onClick={handleFreeCTAClick}
              >
                {ctaText}
              </FreeCTA>
              <KeepCitingCTA onClick={closeHackyModal}>
                {FREE_STYLE_TEXT} {currentStyleName}
              </KeepCitingCTA>
            </AppLink>
          </ModalContents>
          {modalData.withAuthBanner && !isLoggedIn ? (
            <HackyFooter>
              <SmallNotificationBanner
                message={FOOTER_BANNER_TEXT}
                ctaText={FOOTER_BANNER_CTA_TEXT}
                onSignInClick={onSignInClick}
              />
            </HackyFooter>
          ) : null}
        </>
      </HackyModal>
    </ClientOnlyPortal>
  )
}

const mapStateToProps = (state: AppState) => ({
  isLoggedIn: loggedInSelector(state),
  modalData: modalDataSelector(state),
  projectStyle: currentStyleSelector(state),
  currentSourceType: createCitationSourceTypeSelector(state),
})

const mapDispatchToProps = {
  closeModal,
  writingFormSubmit,
  formSubmitStopped,
  formSubmitStarted,
  emitComponentViewEvent,
  userClick,
  startAuthFlow,
}

export default connect(mapStateToProps, mapDispatchToProps)(UCFUpgradeModal)
